import { LoadingButton as Button, LoadingButtonProps } from '@mui/lab'
import {
  alpha,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  Slide,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import {
  cloneElement,
  forwardRef,
  MouseEvent,
  ReactElement,
  useEffect,
  useState,
} from 'react'
import {
  IoCheckmarkOutline,
  IoCloseOutline,
  IoInformationCircleOutline,
  IoSaveOutline,
  IoTrashOutline,
  IoWarningOutline,
} from 'react-icons/io5'

import { useSuccess } from '@/hooks'
import { noop } from '@/utils'

type ConfirmVariant = 'default' | 'destructive' | 'warning' | 'info' | 'success' | 'save'

type ConfirmButtonProps = {
  title: string
  disabled?: boolean
  body?: string | ReactElement
  triggerProps?: LoadingButtonProps
  onCancel?: () => void
  onConfirm?: () => void | Promise<void>
  loading?: boolean
  success?: boolean
  confirmText?: string
  confirmDisabled?: boolean
  cancelText?: string
  maxWidth?: DialogProps['maxWidth']
  successMessage?: string
  trigger?: ReactElement
  sx?: SxProps<Theme>
  variant?: ConfirmVariant
  showIcon?: boolean
  enableKeyboardShortcuts?: boolean
  preventBackdropClose?: boolean
  tooltip?: string
}

const SlideTransition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />
})

function ConfirmButton(props: ConfirmButtonProps) {
  const {
    title,
    onCancel = noop,
    onConfirm = noop,
    loading = false,
    body,
    disabled = false,
    triggerProps = {},
    confirmText,
    cancelText = 'Cancelar',
    confirmDisabled = false,
    maxWidth = 'md',
    success = false,
    successMessage,
    trigger,
    sx,
    variant = 'default',
    showIcon = true,
    enableKeyboardShortcuts = true,
    preventBackdropClose = false,
    tooltip,
  } = props

  const [open, setOpen] = useState(false)
  const theme = useTheme()

  const variantConfig = {
    default: {
      icon: <IoCheckmarkOutline size={24} />,
      color: theme.palette.primary.main,
      confirmText: confirmText || 'Confirmar',
      buttonColor: 'primary' as const,
    },
    destructive: {
      icon: <IoTrashOutline size={24} />,
      color: theme.palette.error.main,
      confirmText: confirmText || 'Excluir',
      buttonColor: 'error' as const,
    },
    warning: {
      icon: <IoWarningOutline size={24} />,
      color: theme.palette.warning.main,
      confirmText: confirmText || 'Continuar',
      buttonColor: 'warning' as const,
    },
    info: {
      icon: <IoInformationCircleOutline size={24} />,
      color: theme.palette.info.main,
      confirmText: confirmText || 'OK',
      buttonColor: 'info' as const,
    },
    success: {
      icon: <IoCheckmarkOutline size={24} />,
      color: theme.palette.success.main,
      confirmText: confirmText || 'Confirmar',
      buttonColor: 'success' as const,
    },
    save: {
      icon: <IoSaveOutline size={24} />,
      color: theme.palette.primary.main,
      confirmText: confirmText || 'Salvar',
      buttonColor: 'primary' as const,
    },
  }

  const config = variantConfig[variant]

  const handleClose = () => {
    onCancel()
    setOpen(false)
  }

  const handleConfirm = async () => {
    try {
      await onConfirm()
    } catch (error) {
      throw new Error(error as string)
    }
  }

  const handleBackdropClick = (event: MouseEvent) => {
    if (preventBackdropClose) {
      event.stopPropagation()
      return
    }
    handleClose()
  }

  useEffect(() => {
    if (!open || !enableKeyboardShortcuts) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        handleClose()
      } else if (event.key === 'Enter' && !confirmDisabled && !loading) {
        event.preventDefault()
        handleConfirm()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, loading, confirmDisabled, enableKeyboardShortcuts])

  useSuccess(success, successMessage || '', handleClose)

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setOpen(true)
  }

  const renderTrigger = trigger ? (
    cloneElement(trigger, { onClick: handleOpen } as any)
  ) : (
    <Button disabled={disabled} {...triggerProps} onClick={handleOpen} sx={sx} />
  )

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} disableInteractive>
          {renderTrigger}
        </Tooltip>
      ) : (
        renderTrigger
      )}

      <Dialog
        maxWidth={maxWidth}
        fullWidth
        open={open}
        onClose={preventBackdropClose ? undefined : handleClose}
        slots={{
          transition: SlideTransition,
        }}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            backgroundColor: 'transparent',
          },
        }}
        slotProps={{
          backdrop: {
            onClick: handleBackdropClick,
            sx: {
              backgroundColor: alpha(theme.palette.common.black, 0.6),
              backdropFilter: 'blur(4px)',
            },
          },
        }}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            position: 'relative',
          }}>
          <Box
            sx={{
              position: 'relative',
              background: `linear-gradient(135deg, ${alpha(
                config.color,
                0.05,
              )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
            }}>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                pb: 2,
                pr: 6,
              }}>
              {showIcon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: alpha(config.color, 0.1),
                    color: config.color,
                    flexShrink: 0,

                    '& svg': {
                      display: 'block',
                    },
                  }}>
                  {config.icon}
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    lineHeight: 1.2,
                  }}>
                  {title}
                </Typography>
                {variant === 'destructive' && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}>
                    Ação irreversível
                  </Typography>
                )}
              </Box>
            </DialogTitle>

            <IconButton
              onClick={handleClose}
              disabled={loading}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: theme.palette.text.secondary,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                },

                '& svg': {
                  display: 'block',
                },
              }}>
              <IoCloseOutline size={20} />
            </IconButton>
          </Box>

          {body && (
            <DialogContent sx={{ py: 3 }}>
              <Fade in timeout={300}>
                <Box>
                  {typeof body === 'string' ? (
                    <DialogContentText
                      sx={{
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        color: theme.palette.text.primary,
                      }}>
                      {body}
                    </DialogContentText>
                  ) : (
                    body
                  )}
                </Box>
              </Fade>
            </DialogContent>
          )}

          <Divider />

          <DialogActions
            sx={{
              p: 3,
              gap: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              borderBottomLeftRadius: 3,
              borderBottomRightRadius: 3,
            }}>
            <Button
              onClick={handleClose}
              disabled={loading}
              size="large"
              sx={{
                minWidth: 100,
                fontWeight: 600,
                textTransform: 'none',
              }}>
              {cancelText}
            </Button>

            <Button
              disabled={confirmDisabled}
              variant="contained"
              onClick={handleConfirm}
              color={config.buttonColor}
              loading={loading}
              size="large"
              sx={{
                minWidth: 120,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                transition: 'all 0.2s ease',
              }}>
              {config.confirmText}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}

export default ConfirmButton
